// UrgentScrapRequest.js
import React, { useEffect } from "react";
import { Form, Button, FloatingLabel } from "react-bootstrap";
import theme from "../../styles/theme/theme";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  clearBuyerMessages,
  fetchAllProducts,
  fetchSubcategories,
  storeReverseBid,
} from "../../features/buyer/buyerSlice";
import { useFormik } from "formik";
import { FaTimes } from "react-icons/fa";

const UrgentScrapRequest = ({ showAlert, show = false, onHide }) => {
  const dispatch = useDispatch();
  const {
    reverseBidsLoading,
    reverseBidsError,
    reverseBidsStoreSuccess,
    products,
    subcategories,
  } = useSelector((state) => state.buyer);

  const productCategories = products?.data?.categories || [];

  // fetch categories on mount
  useEffect(() => {
    dispatch(fetchAllProducts());
  }, [dispatch]);

  const validationSchema = Yup.object({
    scrap_type: Yup.string().required("Required"),
    quantity: Yup.number().required("Required").min(1, "Must be at least 1"),
    unit: Yup.string().required("Required"),
    price: Yup.number()
      .required("Required")
      .min(0, "Must be 0 or greater")
      .typeError("Price must be a number"),
    description: Yup.string().required("Required"),
    category_id: Yup.string().required("Required"),
    subcategory_id: Yup.string().required("Required"),
    location: Yup.string().required("Required"),
    preferred_delivery_date: Yup.string().required("Required"),
  });

  const formik = useFormik({
    initialValues: {
      scrap_type: "",
      quantity: "",
      unit: "",
      price: "",
      description: "",
      category_id: "",
      subcategory_id: "",
      location: "",
      preferred_delivery_date: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const res = await dispatch(storeReverseBid(values)).unwrap();
        resetForm();
        // showAlert(res.message || "Request sent successfully!", "success");
        // onHide(); // hide form via parent
      } catch (err) {
        // onHide();
        // showAlert(err?.message || "Something went wrong.", "error");
      }
    },
  });

  useEffect(() => {
    if (formik.values.category_id) {
      dispatch(fetchSubcategories(formik.values.category_id));
    }
  }, [formik.values.category_id, dispatch]);

  // Clear success message when closing the form
  const handleClose = () => {
    // setSuccessMessage("");
    onHide();
    dispatch(clearBuyerMessages());
  };

  return (
    <>
      <div className="urgent-wrapper" aria-hidden={!show}>
        {/* FORM */}
        <div
          className={`urgent-form-container ${show ? "open" : "closed"}`}
          aria-hidden={!show}
        >
          <div className="urgent-form-inner">
            <h5 style={{ marginBottom: 6, color: theme.palette.primary.main }}>
              Urgent Scrap Request
              <FaTimes
                style={{
                  float: "right",
                  cursor: "pointer",
                  fontSize: "1.2rem",
                  color: theme.palette.primary.main,
                }}
                onClick={handleClose} // Close the form when clicked
              />
            </h5>
            <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: 12 }}>
              Submit your urgent scrap requirement to notify sellers
              immediately.
            </p>

            {reverseBidsError && (
              <div
                role="alert"
                style={{
                  background: "#fdecea",
                  color: "#611a15",
                  padding: "8px 12px",
                  borderRadius: 8,
                  marginBottom: 12,
                  border: "1px solid #f5c6cb",
                }}
              >
                {reverseBidsError}
              </div>
            )}

            {reverseBidsStoreSuccess && (
              <div
                role="status"
                style={{
                  background: "#e6f4ea",
                  color: "#0b6b2d",
                  padding: "8px 12px",
                  borderRadius: 8,
                  marginBottom: 12,
                  border: "1px solid #c3e6cb",
                }}
              >
                {reverseBidsStoreSuccess}
              </div>
            )}

            <Form noValidate onSubmit={formik.handleSubmit}>
              <div className="form-grid">
                {/* Category */}
                <FloatingLabel className="mb-3">
                  <Form.Select
                    name="category_id"
                    value={formik.values.category_id}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.category_id && !!formik.errors.category_id
                    }
                  >
                    <option value="">Select Category</option>
                    {productCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.category_id}
                  </Form.Control.Feedback>
                </FloatingLabel>

                {/* Subcategory */}
                <FloatingLabel className="mb-3">
                  <Form.Select
                    name="subcategory_id"
                    value={formik.values.subcategory_id}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.subcategory_id &&
                      !!formik.errors.subcategory_id
                    }
                    disabled={!formik.values.category_id}
                  >
                    <option value="">Select Subcategory</option>
                    {subcategories.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.subcategory_id}
                  </Form.Control.Feedback>
                </FloatingLabel>

                {/* Scrap Type */}
                <FloatingLabel label="Scrap Type" className="mb-3">
                  <Form.Control
                    type="text"
                    name="scrap_type"
                    value={formik.values.scrap_type}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.scrap_type && !!formik.errors.scrap_type
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.scrap_type}
                  </Form.Control.Feedback>
                </FloatingLabel>

                {/* Quantity */}
                <FloatingLabel label="Quantity" className="mb-3">
                  <Form.Control
                    type="number"
                    name="quantity"
                    value={formik.values.quantity}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.quantity && !!formik.errors.quantity
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.quantity}
                  </Form.Control.Feedback>
                </FloatingLabel>

                {/* Unit */}
                <FloatingLabel className="mb-3">
                  <Form.Select
                    name="unit"
                    value={formik.values.unit}
                    onChange={formik.handleChange}
                    isInvalid={formik.touched.unit && !!formik.errors.unit}
                  >
                    <option value="">Select Unit</option>
                    <option value="kg">Kilogram (kg)</option>
                    <option value="mt">Metric Ton (mt)</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.unit}
                  </Form.Control.Feedback>
                </FloatingLabel>

                {/* Price */}
                <FloatingLabel label="Expected Price (AUD)" className="mb-3">
                  <Form.Control
                    type="number"
                    name="price"
                    value={formik.values.price}
                    onChange={formik.handleChange}
                    isInvalid={formik.touched.price && !!formik.errors.price}
                    placeholder="Enter expected price per unit"
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.price}
                  </Form.Control.Feedback>
                </FloatingLabel>

                {/* Location */}
                <FloatingLabel label="Location" className="mb-3">
                  <Form.Control
                    type="text"
                    name="location"
                    value={formik.values.location}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.location && !!formik.errors.location
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.location}
                  </Form.Control.Feedback>
                </FloatingLabel>

                {/* Preferred Date */}
                <FloatingLabel label="Preferred Delivery Date" className="mb-3">
                  <Form.Control
                    type="date"
                    name="preferred_delivery_date"
                    value={formik.values.preferred_delivery_date}
                    onChange={formik.handleChange}
                    isInvalid={
                      formik.touched.preferred_delivery_date &&
                      !!formik.errors.preferred_delivery_date
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.preferred_delivery_date}
                  </Form.Control.Feedback>
                </FloatingLabel>

                {/* Description */}
                <div className="form-full">
                  <FloatingLabel label="Description" className="mb-3">
                    <Form.Control
                      as="textarea"
                      style={{ height: 90 }}
                      name="description"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        formik.touched.description &&
                        !!formik.errors.description
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.description}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </div>
              </div>
              <div className="right-actions ">
                <Button
                  variant="secondary"
                  onClick={() => {
                    formik.resetForm();
                    onHide();
                  }}
                  style={{ marginRight: 8 }}
                >
                  Cancel
                </Button>

                <Button
                  variant="success"
                  type="submit"
                  disabled={reverseBidsLoading}
                >
                  {reverseBidsLoading ? "Submitting..." : "Submit Request"}
                </Button>
              </div>

              {/* <div className="form-actions">
                
              </div> */}
            </Form>
          </div>
        </div>
      </div>

      {/* Embedded styles */}
      <style>{`
        /* bounce for floating label */
        @keyframes bounce {
          0%, 100% { transform: translateY(-50%) translateX(0); }
          50% { transform: translateY(-50%) translateX(-6px); }
        }

        /* wrapper keeps the + button fixed at bottom-right and form to its left */
        .urgent-wrapper {
          position: fixed;
          bottom: 180px;
          right: 100px;
          display: flex;
          align-items: flex-end;
          gap: 12px; /* gap between form and button */
          z-index: 1500;
        }

        /* FORM PANEL - preserved scale/pop animation */
        .urgent-form-container {
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.18);
          max-width: 720px; /* wider layout */
          width: 100%;
          overflow-y: auto;
          max-height: calc(100vh - 140px); /* keep it below header */
          transform-origin: right bottom;
          transform: scale(0);
          opacity: 0;
          transition: transform 320ms cubic-bezier(.2,.9,.2,1), opacity 220ms ease;
          pointer-events: none;
        }

        /* OPEN: same "pop" animation (scale 0 -> 1) */
        .urgent-form-container.open {
          transform: scale(1);
          opacity: 1;
          pointer-events: auto;
        }

        .urgent-form-container.closed {
          display: none !important; /* fully remove from layout */
        }

        .urgent-form-inner {
          padding: 16px;
        }

        /* grid layout: 2 columns on wide, 1 column on small */
        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          align-items: start;
        }

        /* description spans full width */
        .form-full {
          grid-column: 1 / -1;
        }

        /* control heights */
        .urgent-form-inner .form-control,
        .urgent-form-inner .form-select {
          height: calc(2.6rem + 2px);
          padding: 0.45rem 0.75rem;
          font-size: 0.95rem;
        }

        .urgent-form-inner textarea.form-control {
          height: 90px;
          resize: vertical;
        }

        .form-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 10px;
        }

        .form-actions .right-actions {
          display: flex;
          align-items: center;
        }

        .right-actions {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  margin-top: 16px !important;
  justify-content: flex-end !important;
}

.right-actions .btn {
  margin: 0 !important;
}

        /* floating button container aligned at right */
        .urgent-floating-button {
          position: relative; /* not fixed; wrapper already fixed */
          display: flex;
          align-items: center;
        }

        /* floating label (optional) - hidden on very small screens */
        .urgent-floating-label {
          position: absolute;
          right: 70px;
          top: 50%;
          transform: translateY(-50%);
          background: ${theme.palette.tertiary.main};
          color: #fff;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.85rem;
          white-space: nowrap;
          animation: bounce 2s infinite;
          clip-path: polygon(100% 50%, 90% 0%, 0% 0%, 0% 100%, 90% 100%);
        }

        /* FAB */
        .floating-btn {
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 200ms ease;
          outline: none;
          width: 56px;
          height: 56px;
        }

        .floating-btn .plus-icon {
          color: #fff;
          transition: transform 240ms ease;
          display: inline-block;
          line-height: 1;
          font-size: 20px;
        }

        .floating-btn.open .plus-icon {
          transform: rotate(45deg);
        }

        .floating-btn:hover {
          transform: translateY(-2px);
        }

        /* responsive */
        @media (max-width: 992px) {
          .form-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .urgent-form-container {
            max-width: 520px;
          }
        }

        @media (max-width: 576px) {
          .urgent-wrapper {
            right: 12px;
            bottom: 100px;
            gap: 10px;
          }

          .urgent-form-container {
            max-width: 92vw;
            max-height: calc(100vh - 250px);
          }

          .form-grid {
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .urgent-form-inner {
            padding: 12px;
          }

          .urgent-form-inner .form-control,
          .urgent-form-inner .form-select {
            height: calc(2.4rem + 2px);
            padding: 0.35rem 0.6rem;
            font-size: 0.9rem;
          }

          .urgent-form-inner textarea.form-control {
            height: 80px;
          }

          .urgent-floating-label { display: none; }
        }
      `}</style>
    </>
  );
};

export default UrgentScrapRequest;
